import {
    FIT_MARGIN,
    MIN_ZOOM,
    PAGE_GAP,
    PAGE_PADDING,
    ZOOM_ANIMATION_MS,
    ZOOM_EPSILON,
    ZOOM_REFERENCE_LINE,
    ZOOM_SETTLE_MS,
    ZOOM_STEPS,
    MAX_RENDER_ZOOM,
} from '../constants';
import { anchoredScrollLeft } from '../helpers/gestures';
import { FitMode, PageInfo } from '../types';

const CUBIC = 3;
const HALF = 2;
const PADDING_SIDES = 2;

export interface ZoomControllerOptions {
    scroller: HTMLElement;
    pagesElement: HTMLElement;
    pages: PageInfo[];
    dpr: number;
    getCurrent: () => number;
    onFitModeChange: (mode: FitMode | null) => void;
    initialFitMode?: FitMode;
    ignoreHeightChanges?: boolean;
}

export interface ZoomAnchor {
    x?: number;
    y?: number;
}

export const computeTops = (pages: PageInfo[], zoom: number): number[] => {
    const tops: number[] = [];
    let y = PAGE_PADDING;
    pages.forEach((page) => {
        tops.push(y);
        y += page.h * zoom + PAGE_GAP;
    });
    return tops;
};

export const findPageAt = (tops: number[], y: number): number => {
    let low = 0;
    let high = tops.length - 1;
    while (low < high) {
        const mid = Math.ceil((low + high) / HALF);
        if (tops[mid] <= y) {
            low = mid;
        } else {
            high = mid - 1;
        }
    }
    return low;
};

export const fitZoomFor = (page: PageInfo, viewWidth: number, viewHeight: number, mode: FitMode): number => {
    const byWidth = (viewWidth - FIT_MARGIN) / page.w;
    const byHeight = (viewHeight - PAGE_PADDING * PADDING_SIDES) / page.h;
    return Math.max(MIN_ZOOM, mode === 'width' ? byWidth : Math.min(byWidth, byHeight));
};

export const nextZoomStep = (current: number, direction: 1 | -1): number | undefined => (
    direction > 0
        ? ZOOM_STEPS.find((step) => step > current + ZOOM_EPSILON)
        : [...ZOOM_STEPS].reverse().find((step) => step < current - ZOOM_EPSILON)
);

export interface AnchorResult {
    pageIndex: number;
    fraction: number;
}

export const captureAnchor = (
    pages: PageInfo[],
    tops: number[],
    zoom: number,
    scrollTop: number,
    referenceY: number,
): AnchorResult => {
    const pageIndex = findPageAt(tops, scrollTop + referenceY);
    return { pageIndex, fraction: (scrollTop + referenceY - tops[pageIndex]) / (pages[pageIndex].h * zoom) };
};

export const restoreScrollTop = (
    pages: PageInfo[],
    tops: number[],
    zoom: number,
    anchor: AnchorResult,
    referenceY: number,
): number => tops[anchor.pageIndex] + anchor.fraction * pages[anchor.pageIndex].h * zoom - referenceY;

export class ZoomController {
    zoom = 1;

    tops: number[] = [];

    fitMode: FitMode | null = 'page';

    private readonly maxPageWidth: number;

    private lastClientWidth = 0;

    private readonly settleListeners = new Set<() => void>();

    private readonly targetListeners = new Set<() => void>();

    private animationTarget: number | null = null;

    private settleTimer: number | null = null;

    private animationFrame = 0;

    private resizeFrame = 0;

    private readonly resizeObserver: ResizeObserver;

    constructor(private readonly options: ZoomControllerOptions) {
        const { pagesElement, dpr, initialFitMode = 'page' } = options;
        pagesElement.style.setProperty('--dpr', String(dpr));
        this.fitMode = initialFitMode;
        this.maxPageWidth = Math.max(...options.pages.map((page) => page.w));
        this.lastClientWidth = options.scroller.clientWidth;
        this.zoom = this.clamp(this.fitZoom(initialFitMode));
        pagesElement.style.setProperty('--z', String(this.zoom));
        this.tops = computeTops(options.pages, this.zoom);
        this.resizeObserver = new ResizeObserver(() => {
            const width = options.scroller.clientWidth;
            const hasWidthChanged = width !== this.lastClientWidth;
            this.lastClientWidth = width;
            if (options.ignoreHeightChanges && !hasWidthChanged) {
                return;
            }
            cancelAnimationFrame(this.resizeFrame);
            this.resizeFrame = requestAnimationFrame(() => this.refit());
        });
        this.resizeObserver.observe(options.scroller);
    }

    onSettle(listener: () => void): () => void {
        this.settleListeners.add(listener);
        return () => {
            this.settleListeners.delete(listener);
        };
    }

    onTarget(listener: () => void): () => void {
        this.targetListeners.add(listener);
        return () => {
            this.targetListeners.delete(listener);
        };
    }

    get renderTarget(): number {
        return this.animationTarget ?? this.zoom;
    }

    fitZoom(mode: FitMode): number {
        const { pages, scroller, getCurrent } = this.options;
        const page = pages[getCurrent()] ?? pages[0];
        return fitZoomFor(page, scroller.clientWidth, scroller.clientHeight, mode);
    }

    setZoom(value: number, mode: FitMode | null, anchorY?: number, anchorX?: number): void {
        this.animationTarget = null;
        this.applyZoom(value, mode, anchorY, anchorX);
    }

    private applyZoom(value: number, mode: FitMode | null, anchorY?: number, anchorX?: number): void {
        const {
            scroller, pagesElement, pages, onFitModeChange,
        } = this.options;
        const previousZoom = this.zoom;
        const previousScrollLeft = scroller.scrollLeft;
        const referenceY = anchorY === undefined ? scroller.clientHeight * ZOOM_REFERENCE_LINE : anchorY;
        const referenceX = anchorX === undefined ? scroller.clientWidth / HALF : anchorX;
        const anchor = captureAnchor(pages, this.tops, this.zoom, scroller.scrollTop, referenceY);
        this.fitMode = mode;
        this.zoom = this.clamp(value);
        onFitModeChange(mode);
        pagesElement.style.setProperty('--z', String(this.zoom));
        this.tops = computeTops(pages, this.zoom);
        scroller.scrollTop = restoreScrollTop(pages, this.tops, this.zoom, anchor, referenceY);
        scroller.scrollLeft = anchoredScrollLeft({
            scrollLeft: previousScrollLeft,
            anchorX: referenceX,
            clientWidth: scroller.clientWidth,
            pageWidth: this.maxPageWidth,
            oldZoom: previousZoom,
            newZoom: this.zoom,
        });
        this.scheduleSettle();
    }

    animateZoom(target: number, mode: FitMode | null, anchor?: ZoomAnchor): void {
        cancelAnimationFrame(this.animationFrame);
        const from = this.zoom;
        const to = this.clamp(target);
        this.animationTarget = null;
        if (document.hidden || Math.abs(to - from) < ZOOM_EPSILON) {
            this.setZoom(to, mode, anchor?.y, anchor?.x);
            return;
        }
        this.animationTarget = to;
        this.targetListeners.forEach((listener) => listener());
        const start = performance.now();
        const tick = (now: number): void => {
            const progress = Math.min((now - start) / ZOOM_ANIMATION_MS, 1);
            const eased = 1 - (1 - progress) ** CUBIC;
            this.applyZoom(from + (to - from) * eased, progress === 1 ? mode : null, anchor?.y, anchor?.x);
            if (progress < 1) {
                this.animationFrame = requestAnimationFrame(tick);
            } else {
                this.animationTarget = null;
            }
        };
        this.animationFrame = requestAnimationFrame(tick);
    }

    refit(): void {
        if (this.fitMode && this.options.pages.length) {
            this.setZoom(this.fitZoom(this.fitMode), this.fitMode);
        }
    }

    stepZoom(direction: 1 | -1): void {
        const next = nextZoomStep(this.zoom, direction);
        if (next) {
            this.animateZoom(next, null);
        }
    }

    toggleFit(): void {
        const mode: FitMode = this.fitMode === 'page' ? 'width' : 'page';
        this.animateZoom(this.fitZoom(mode), mode);
    }

    wheelZoom(factor: number, anchorY: number): void {
        cancelAnimationFrame(this.animationFrame);
        this.setZoom(this.zoom * factor, null, anchorY);
    }

    pinchZoom(value: number, anchorX: number, anchorY: number): void {
        cancelAnimationFrame(this.animationFrame);
        this.setZoom(value, null, anchorY, anchorX);
    }

    dispose(): void {
        cancelAnimationFrame(this.animationFrame);
        cancelAnimationFrame(this.resizeFrame);
        if (this.settleTimer !== null) {
            window.clearTimeout(this.settleTimer);
        }
        this.resizeObserver.disconnect();
        this.settleListeners.clear();
        this.targetListeners.clear();
    }

    private clamp(value: number): number {
        return Math.min(MAX_RENDER_ZOOM / this.options.dpr, Math.max(MIN_ZOOM, value));
    }

    private scheduleSettle(): void {
        if (this.settleTimer !== null) {
            window.clearTimeout(this.settleTimer);
        }
        this.settleTimer = window.setTimeout(() => {
            this.settleTimer = null;
            this.settleListeners.forEach((listener) => listener());
        }, ZOOM_SETTLE_MS);
    }
}
